/**
 * Timezone utilities for detecting and formatting timezone information
 */
import {
  getBrowserTimezone,
  getDefaultEventDates,
  toWallClock,
  isValidTimezone,
  getOffsetMinutes,
  readDeviceTimezone,
} from './datetime';
import { logger } from './logger';

export interface TimezoneInfo {
  name: string; // IANA timezone name (e.g., "Europe/Lisbon", "America/New_York")
  offset: string; // UTC offset string (e.g., "GMT+00:00", "GMT-05:00")
  offsetMinutes: number; // Offset in minutes from UTC
  city: string; // Display city name (e.g., "Lisbon", "New York")
  isDST: boolean; // Whether DST is currently active
}

export { getBrowserTimezone };

/**
 * Get UTC offset string for a given timezone and date
 * @param timezoneName IANA timezone name (e.g., "Europe/Lisbon")
 * @param date Optional date to check (for DST consideration), defaults to now
 * @returns Offset string like "GMT+00:00" or "GMT-05:00"
 */
export function getTimezoneOffset(timezoneName: string, date: Date = new Date()): string {
  try {
    return formatOffsetMinutes(getOffsetMinutes(timezoneName, date));
  } catch (error) {
    logger.warn('Failed to get timezone offset:', error);
    return 'GMT+00:00';
  }
}

/**
 * Get timezone offset in minutes
 */
export function getTimezoneOffsetMinutes(timezoneName: string, date: Date = new Date()): number {
  return getOffsetMinutes(timezoneName, date);
}

/**
 * Format offset minutes to GMT string
 */
export function formatOffsetMinutes(minutes: number): string {
  const sign = minutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(minutes);
  const hours = Math.floor(absMinutes / 60)
    .toString()
    .padStart(2, '0');
  const mins = (absMinutes % 60).toString().padStart(2, '0');
  return `GMT${sign}${hours}:${mins}`;
}

/**
 * City name for a zone id: the last segment with underscores as spaces
 * ("America/New_York" → "New York"); ids without a slash ("UTC") pass through.
 */
export function getTimezoneCity(timezoneName: string): string {
  const parts = timezoneName.split('/');
  return parts.length >= 2 ? parts[parts.length - 1].replace(/_/g, ' ') : timezoneName;
}

/**
 * Get complete timezone information
 */
export function getTimezoneInfo(timezoneName?: string, date: Date = new Date()): TimezoneInfo {
  const tz = timezoneName ?? getBrowserTimezone();
  const offset = getTimezoneOffset(tz, date);
  const offsetMinutes = getTimezoneOffsetMinutes(tz, date);
  const city = getTimezoneCity(tz);

  // Check if DST is active (works for both hemispheres)
  // Standard time has minimum offset; DST has larger offset
  const january = new Date(date.getFullYear(), 0, 1);
  const july = new Date(date.getFullYear(), 6, 1);
  const janOffset = getTimezoneOffsetMinutes(tz, january);
  const julyOffset = getTimezoneOffsetMinutes(tz, july);
  const isDST = offsetMinutes !== Math.min(janOffset, julyOffset);

  return {
    name: tz,
    offset,
    offsetMinutes,
    city,
    isDST,
  };
}

const canonicalCache = new Map<string, string>();

/**
 * The runtime's canonical spelling of a zone, used only to COMPARE names.
 *
 * tz-lookup returns modern names ("Asia/Kolkata", "Europe/Kyiv") while
 * `Intl.supportedValuesOf` returns ICU's legacy ones ("Asia/Calcutta",
 * "Europe/Kiev"). Both resolve to the same canonical id in one runtime, so the
 * picker can find the row for a venue-derived zone. Cached: the picker compares
 * ~420 names per render.
 */
export function normalizeTimezone(tz: string): string {
  const cached = canonicalCache.get(tz);
  if (cached !== undefined) return cached;
  let canonical = tz;
  try {
    canonical = new Intl.DateTimeFormat('en-US', { timeZone: tz }).resolvedOptions().timeZone;
  } catch {
    // Unresolvable: keep the input so comparisons still work by string.
  }
  canonicalCache.set(tz, canonical);
  return canonical;
}

const allTimezonesCache = new Map<string, TimezoneInfo[]>();

const byOffsetThenCity = (a: TimezoneInfo, b: TimezoneInfo) =>
  a.offsetMinutes - b.offsetMinutes || a.city.localeCompare(b.city);

/**
 * Zones `Intl.supportedValuesOf('timeZone')` returns under ICU's legacy
 * canonical spelling instead of the current IANA name (holds on Node 22 / ICU 76
 * and Node 24.21 / ICU 78 — see tc39/ecma402#806: browsers agree). Each key is
 * present in `Intl.supportedValuesOf('timeZone')`; each value is NOT (ICU
 * still resolves it, just doesn't enumerate it) — confirmed locally rather
 * than assumed, since ICU data drifts between runtimes/versions.
 *
 * `normalizeTimezone` canonicalizes BOTH spellings to the legacy id (ICU's
 * own canonical), so this map only changes what a row is LABELED; it does not
 * change which row a given `include`/`normalizeTimezone` comparison lands on.
 */
const LEGACY_TO_MODERN_ZONE: Record<string, string> = {
  'Europe/Kiev': 'Europe/Kyiv',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'America/Godthab': 'America/Nuuk',
  'Pacific/Enderbury': 'Pacific/Kanton',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Pacific/Truk': 'Pacific/Chuuk',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'America/Catamarca': 'America/Argentina/Catamarca',
  'America/Cordoba': 'America/Argentina/Cordoba',
  'America/Jujuy': 'America/Argentina/Jujuy',
  'America/Mendoza': 'America/Argentina/Mendoza',
  'America/Indianapolis': 'America/Indiana/Indianapolis',
  'America/Louisville': 'America/Kentucky/Louisville',
};

/** `tz`, or its modern spelling when `tz` is a known legacy id. */
const toModernZone = (tz: string): string => LEGACY_TO_MODERN_ZONE[tz] ?? tz;

/**
 * Every zone the runtime knows (`Intl.supportedValuesOf('timeZone')`, ~420),
 * with offsets computed at `at` — the event's start — so a July event shows its
 * summer offset even when created in January. Renamed zones (`LEGACY_TO_MODERN_ZONE`)
 * are relabeled to their modern name/city so unselected rows and search both
 * use the spelling a venue lookup or a freshly-typed query would produce.
 * Sorted by offset, then city.
 *
 * `include` guarantees zones the caller cannot do without (the selected one):
 * a zone missing from the runtime list ("UTC" on Node) is added, and either
 * spelling of a renamed zone REPLACES that row under its modern name, so a
 * venue-derived "Asia/Kolkata" — or an old stored event's "Asia/Calcutta" —
 * both show as Kolkata rather than Calcutta. Invalid values are ignored.
 *
 * The base list is cached per UTC calendar day of `at`.
 */
export function getAllTimezones(
  at: Date,
  include: readonly (string | null | undefined)[] = [],
): TimezoneInfo[] {
  const dayKey = at.toISOString().slice(0, 10);
  let base = allTimezonesCache.get(dayKey);
  if (!base) {
    // Dedupe by canonical zone (`normalizeTimezone`) rather than by the raw
    // enumerated id: a runtime that lists BOTH spellings of a renamed zone
    // (e.g. `Asia/Calcutta` and `Asia/Kolkata`) would otherwise yield two
    // identical-looking rows. `normalizeTimezone` is the same key the
    // `include`-replacement merge below uses, so dedup and replacement agree
    // on which rows are "the same zone" regardless of enumeration order —
    // `toModernZone` always relabels a legacy id to its modern spelling no
    // matter which spelling is kept.
    const seen = new Set<string>();
    const deduped: TimezoneInfo[] = [];
    for (const tz of Intl.supportedValuesOf('timeZone')) {
      const key = normalizeTimezone(tz);
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(getTimezoneInfo(toModernZone(tz), at));
    }
    base = deduped;
    allTimezonesCache.set(dayKey, base);
  }

  const replacements = new Map<string, TimezoneInfo>();
  for (const tz of include) {
    if (!tz || !isValidTimezone(tz)) continue;
    const key = normalizeTimezone(tz);
    if (!replacements.has(key)) replacements.set(key, getTimezoneInfo(toModernZone(tz), at));
  }
  if (replacements.size === 0) return [...base].sort(byOffsetThenCity);

  const merged = base.map(info => {
    const key = normalizeTimezone(info.name);
    const replacement = replacements.get(key);
    if (replacement) replacements.delete(key);
    return replacement ?? info;
  });
  return [...merged, ...replacements.values()].sort(byOffsetThenCity);
}

const foldText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

// "gmt+2", "+02", "utc5:30", "+0530", and "+05:" / "+05:3" while the minutes are typed.
// Bare digits ("5") are not an offset: they need a sign or a gmt/utc prefix.
const OFFSET_QUERY = /^(?:(gmt|utc)([+-]?)|([+-]))(\d{1,2})(?:(:)(\d{0,2})|(\d{2}))?$/;

// An offset search before its hours: "gm", "gmt", "utc-", "+".
const PARTIAL_OFFSET_QUERY = /^(?:(?:gm|gmt|ut|utc)([+-]?)|([+-]))$/;

/**
 * Legacy city names a stored/typed search might still use — derived from
 * `LEGACY_TO_MODERN_ZONE` so the two maps can't drift apart. "Kiev" and
 * "Calcutta" keep finding their zone even though the base list (and the
 * backend, for old events) may carry either spelling.
 */
const LEGACY_ZONE_ALIASES: Record<string, string[]> = Object.fromEntries(
  Object.entries(LEGACY_TO_MODERN_ZONE).map(([legacy, modern]) => [
    modern,
    [getTimezoneCity(legacy)],
  ]),
);

/**
 * Search terms for a zone that aren't derivable from its id or its legacy
 * alias — cities that share a country-wide zone with the id's own city.
 * Minimal and uncontroversial by design; extend sparingly.
 */
const ZONE_SEARCH_ALIASES: Record<string, string[]> = {
  'Asia/Kolkata': ['Mumbai', 'Delhi', 'Bangalore'],
};

const zoneSearchAliases = (name: string): string[] => [
  ...(LEGACY_ZONE_ALIASES[name] ?? []),
  ...(ZONE_SEARCH_ALIASES[name] ?? []),
];

/**
 * Filter for the picker's search box: city, zone id (underscores as spaces),
 * offset, and search aliases (legacy names like "Calcutta", and cities like
 * "Mumbai" that share a zone with a differently-named id — never shown in
 * labels, matched here only). Case- and accent-insensitive ("sao paulo" finds
 * São Paulo). An offset query ("GMT+2", "+02", "UTC+5:30") matches by value,
 * so "GMT+3" does not also return GMT+03:30 zones through a substring match.
 * A half-typed offset never empties the list: "GMT" keeps every zone, "GMT+"
 * the zones at or ahead of GMT, and "+05:" every GMT+05:xx zone.
 */
export function filterTimezones(list: readonly TimezoneInfo[], query: string): TimezoneInfo[] {
  const q = foldText(query.trim());
  if (!q) return [...list];

  // Spaces dropped ("GMT +2"), and a typographic minus or dash read as "-".
  const compact = q.replace(/\s+/g, '').replace(/[−–]/g, '-');

  const partial = PARTIAL_OFFSET_QUERY.exec(compact);
  if (partial) {
    // Unmatched groups are undefined at runtime, though typed as string.
    const [, prefixedSign, bareSign] = partial;
    const sign = prefixedSign || bareSign;
    if (!sign) return [...list];
    return list.filter(info => (sign === '-' ? info.offsetMinutes < 0 : info.offsetMinutes >= 0));
  }

  const offset = OFFSET_QUERY.exec(compact);
  if (offset) {
    const [, , prefixedSign, bareSign, hours, colon, typedMinutes, minutes] = offset;
    const sign = (prefixedSign || bareSign) === '-' ? -1 : 1;
    if (!colon) {
      const target = sign * (Number(hours) * 60 + Number(minutes || '0'));
      return list.filter(info => info.offsetMinutes === target);
    }
    // "+05:" and "+05:3": that hour, with minutes starting as typed.
    return list.filter(info => {
      const abs = Math.abs(info.offsetMinutes);
      return (
        (info.offsetMinutes === 0 || Math.sign(info.offsetMinutes) === sign) &&
        Math.floor(abs / 60) === Number(hours) &&
        String(abs % 60)
          .padStart(2, '0')
          .startsWith(typedMinutes)
      );
    });
  }

  return list.filter(info =>
    foldText(
      [info.city, info.name.replace(/_/g, ' '), ...zoneSearchAliases(info.name)].join(' '),
    ).includes(q),
  );
}

/**
 * Typed outcome of validating a (potentially-untrusted) IANA timezone
 * string. Two cases:
 *
 *   - `resolved`: the input was a usable timezone (valid IANA or null/
 *     undefined, treated as "no source preference, use browser tz").
 *   - `fell_back_to_browser`: the input was a non-null string that
 *     failed validation. The caller almost certainly wants to surface
 *     this to the user (e.g. soft toast: "we couldn't read this event's
 *     timezone — showing times in your local timezone").
 *
 * Why the split: silently substituting browser-local for a malformed
 * source timezone is the textbook "fallback masks the real problem"
 * pattern. The form picker would show one tz label while the wall-clock
 * strings were rendered in another. By making the fallback a tagged
 * result, callers can choose to inform the user.
 */
export type TimezoneResolution =
  | { kind: 'resolved'; tz: string }
  | { kind: 'fell_back_to_browser'; requestedTz: string; browserTz: string };

/**
 * Validate an IANA timezone string via `isValidTimezone` (Temporal probe) —
 * the only reliable way to detect a bad IANA name before passing it to
 * user-facing rendering.
 *
 * Treats null/undefined as a soft "no source preference" — returns
 * `kind: 'resolved'` with the browser's timezone, no fallback signal.
 * Only a non-null string that fails the probe produces
 * `kind: 'fell_back_to_browser'`.
 */
export function resolveTimezone(requested: string | null | undefined): TimezoneResolution {
  const browserTz = getBrowserTimezone();
  if (requested == null) return { kind: 'resolved', tz: browserTz };
  if (isValidTimezone(requested)) return { kind: 'resolved', tz: requested };
  return { kind: 'fell_back_to_browser', requestedTz: requested, browserTz };
}

/**
 * Outcome of cross-checking the platform's two independent answers to "where is
 * this user".
 *
 * `mismatched` means the browser contradicted itself and one of the two signals
 * is a lie — we cannot tell which, only that the IANA name we build everything
 * on is no longer trustworthy.
 */
export type TimezoneConsistency =
  | { kind: 'consistent'; tz: string; offsetMinutes: number }
  | {
      kind: 'mismatched';
      /** The IANA name the platform reported, and what we act on everywhere. */
      tz: string;
      /** The offset that name implies at `at`. */
      zoneOffsetMinutes: number;
      /** The offset the clock actually runs at, per `Date`. */
      clockOffsetMinutes: number;
    };

/**
 * Compare the IANA zone id against the raw clock offset.
 *
 * The browser exposes its timezone twice, through paths that normally resolve
 * from the same host setting:
 *
 *   - `Intl.DateTimeFormat().resolvedOptions().timeZone` → an IANA NAME, which
 *     is what `getBrowserTimezone` returns and what every formatter, the create
 *     -event picker, and the stored `event.timezone` are built on.
 *   - `Date.prototype.getTimezoneOffset()` → a bare OFFSET for right now.
 *
 * The name carries strictly more information (an offset is a function of a name
 * plus a date — Cairo is +02:00 in January and +03:00 in September), so the name
 * is the right primitive and nothing here changes that. But a name can be
 * REPLACED by another well-formed name, and no amount of inspecting it will
 * reveal the swap. Fingerprint-resistant browsers do exactly this: Firefox's
 * `privacy.resistFingerprinting` (and LibreWolf / Tor Browser, which ship it on)
 * report `Atlantic/Reykjavik` — a real zone, just not the user's. When the spoof
 * is partial and `Date` keeps the true offset, this comparison is the only
 * signal left that the name is wrong. A stale V8 timezone cache after the OS
 * zone changes mid-session produces the same divergence.
 *
 * Note the sign flip: `getTimezoneOffset()` counts minutes BEHIND UTC (Cairo is
 * `-180` there), while `getOffsetMinutes` counts minutes AHEAD (`+180`).
 *
 * Deliberately returns a tagged result and resolves nothing. An offset cannot be
 * inverted back into a zone — `+03:00` right now is Cairo, Nairobi, Moscow,
 * Riyadh and Baghdad, which agree today and diverge the moment DST rules differ
 * — so "trust the offset instead" would trade a knowably-wrong answer for a
 * plausibly-wrong one. Picking a side is a decision for a caller that can ask
 * the user; see `reportTimezoneConsistency` for the observe-only default.
 *
 * `tz` defaults to the name every formatter uses. `deviceTimezoneForHeader`
 * passes the name it read itself, so the name it checks is the name it sends.
 */
export function detectTimezoneConsistency(
  at: Date = new Date(),
  tz: string = getBrowserTimezone(),
): TimezoneConsistency {
  const zoneOffsetMinutes = getTimezoneOffsetMinutes(tz, at);
  const clockOffsetMinutes = -at.getTimezoneOffset();

  if (zoneOffsetMinutes === clockOffsetMinutes) {
    return { kind: 'consistent', tz, offsetMinutes: zoneOffsetMinutes };
  }
  return { kind: 'mismatched', tz, zoneOffsetMinutes, clockOffsetMinutes };
}

/** The request header that carries the device's zone to the backend on a signed-in API call. */
export const DEVICE_TIMEZONE_HEADER = 'X-Device-Timezone';

/** An offset in place of a zone name: V8 reports `+00:00` for a host set to plain GMT. */
const OFFSET_FORM = /^[+-]/;

/**
 * The zone a signed-in API call sends as `DEVICE_TIMEZONE_HEADER`: the zone name
 * the device's engine reports, when the device's own clock agrees with it at
 * `at`; otherwise undefined, and the call carries no zone.
 *
 * A name the clock contradicts is a partial spoof or a zone read on one side of
 * a move and the clock on the other (see `detectTimezoneConsistency`), and
 * sending it would put the member's night in the wrong place. A full spoof, name
 * and clock replaced together, agrees with itself and is sent: nothing in the
 * page can tell it from a real zone.
 *
 * An offset in place of a name and a name the engine's own zone data refuses are
 * never sent. A legacy link name (V8's `Asia/Calcutta`, where JavaScriptCore says
 * `Asia/Kolkata`) goes as the engine gives it: the backend canonicalises it, as
 * the one place every client reaches.
 *
 * Read on every call, never cached, so the request after a change of zone
 * carries the new one. One path for the browser, the PWA and the native app,
 * whose WebView answers Intl as a browser does.
 */
export function deviceTimezoneForHeader(at: Date = new Date()): string | undefined {
  const zone = readDeviceTimezone();
  if (!zone || OFFSET_FORM.test(zone) || !isValidTimezone(zone)) return undefined;
  return detectTimezoneConsistency(at, zone).kind === 'consistent' ? zone : undefined;
}

/**
 * Observe-only wrapper: log a mismatch once, change nothing.
 *
 * Called at app boot rather than from the create-event form, for two reasons.
 * A bad zone name skews every rendered time in the app (event headers, chat
 * separators, countdowns), not just the one surface where it becomes durable
 * data. And booting is the only moment that samples EVERY session, so the logs
 * answer "how many of our users are affected" instead of "how many affected
 * users happened to open the event composer".
 *
 * Known gap: this fires once per session, so a user who crosses a timezone
 * while the app stays open is not re-checked. That matters more here than in a
 * plain web app — the PWA and the Capacitor shell both stay resident for days.
 * Re-running on foreground (`visibilitychange` / Capacitor's `appStateChange`)
 * is the natural extension if the logs justify it.
 */
export function reportTimezoneConsistency(at: Date = new Date()): void {
  const result = detectTimezoneConsistency(at);
  if (result.kind === 'consistent') return;

  // logger.error, not warn: `logger.warn` is gated behind import.meta.env.DEV, so
  // a warning here would never reach production — and production is the only place
  // the "how many of our users are affected" question can be answered. `error` is
  // the one level that always logs.
  logger.error(
    'Browser timezone is self-contradictory; rendered times may be wrong. ' +
      'Common causes: a script or an extension that replaces the zone name but not the ' +
      'clock, or a zone change that has reached only one of the two.',
    {
      timezone: result.tz,
      zoneOffsetMinutes: result.zoneOffsetMinutes,
      clockOffsetMinutes: result.clockOffsetMinutes,
      deltaMinutes: result.clockOffsetMinutes - result.zoneOffsetMinutes,
    },
  );
}

/**
 * Compute the Create Event form's default start/end datetimes as
 * browser-local wall-clock strings ("YYYY-MM-DDTHH:mm"), based on the
 * current time:
 *   - `start`: the top of the next hour
 *   - `end`:   `start` + 2 hours
 *
 * Why this lives here (vs. inline in CreateEvent.tsx):
 *   - The previous inline implementation used `d.toISOString().slice(0,16)`
 *     which silently converts to UTC. For any user not in UTC, the form
 *     opened at the wrong wall-clock (off by the local offset). Temporal's
 *     ZonedDateTime reads device-local time so the wall-clock matches
 *     what the user actually sees on their clock.
 *   - Exporting as a pure function (with an injected `now` for tests) lets
 *     us pin the behavior under any system timezone via `process.env.TZ`.
 *
 * Caller usage:
 *   const defaultDates = useMemo(() => computeDefaultDates(), []);
 */
export function computeDefaultDates(now: Date = new Date()): { start: string; end: string } {
  return getDefaultEventDates(now);
}

/**
 * Convert a UTC instant (ISO 8601 with offset) into a wall-clock string
 * `YYYY-MM-DDTHH:mm` interpreted in the given IANA timezone. When `tz` is
 * omitted, the conversion happens in the browser's local timezone.
 *
 * Why this exists: the Create Event form stores datetimes as naive
 * "YYYY-MM-DDTHH:mm" strings without a timezone marker, paired with a
 * separate `selectedTimezone` IANA name. For imported events (e.g. Luma),
 * we need the displayed wall-clock to match the source event's local time,
 * NOT the browser's local time. Pass the source timezone here.
 *
 * Returns "" for invalid input so callers can early-return without a
 * try/catch.
 *
 * Examples:
 *   instantToWallClock("2026-06-01T17:30:00.000Z", "Europe/Berlin")
 *     // → "2026-06-01T19:30"  (Berlin is UTC+2 in June with DST)
 *   instantToWallClock("2026-06-01T17:30:00.000Z", "America/New_York")
 *     // → "2026-06-01T13:30"  (NYC is UTC-4 in June with DST)
 *   instantToWallClock("2026-06-01T17:30:00.000Z")
 *     // → wall-clock in the browser's local timezone
 *   instantToWallClock("not-a-date", "Europe/Berlin")
 *     // → ""
 */
export function instantToWallClock(iso: string, tz?: string): string {
  return toWallClock(iso, tz);
}
